import type React from 'react';
import {Fragment} from 'react';
import {Link, Stack, Tooltip, Typography} from '@mui/material';
import {StatChip} from '~/components/StatChip/StatChip';
import {Tag} from '~/components/tag/Tag';
import {COLORS} from '~/style/colors-ts';
import {PageHeader} from '~/components/main/header/PageHeader.tsx';
import DollarCoinIcon from '~/icons/svg/DollarCoinIcon.svg?react';
import DbIcon from '~/icons/svg/DbIcon.svg?react';
import ChartIcon from '~/icons/svg/ChartIcon.svg?react';
import NoteIcon from '~/icons/svg/NoteIcon.svg?react';
import {breadcrumb} from '../domain/exploreTree';
import type {ProfileHeader, RiskFlag} from '../types/categoryProfileTypes';
import {SettingsLink} from '../settings/SettingsLink';
import {CategorySearch} from './CategorySearch';
import {KpiRow} from './components/KpiRow';

const FLAG: Record<RiskFlag, {label: string, tip: (h: ProfileHeader) => string}> = {
    single_source: {
        label: 'Single-source exposure',
        tip: h => `The top supplier holds ${h.kpis.topSupplierSharePct?.toFixed(0)}% of spend (60% or more)`,
    },
    supplier_concentration: {
        label: 'Supplier concentration',
        tip: h => `The top supplier holds ${h.kpis.topSupplierSharePct?.toFixed(0)}% of spend (35% or more)`,
    },
    fast_growth: {label: 'Fast spend growth', tip: h => `Spend is up ${h.kpis.yoyPct?.toFixed(0)}% on a year earlier`},
    tail_spend: {label: 'Tail spend', tip: () => '8 or more suppliers outside the 80% of spend'},
};

type Props = {
    databagId: number;
    header: ProfileHeader;
    money: (v: number) => string;
    onBack: () => void;
    /** Open another category's profile (a parent in the breadcrumb, or a search result). */
    onOpen: (path: string) => void;
};

/**
 * Mithra 3.0 header (spec 4.2): "← Explore Categories / name" with the internal risk flags in the app's top bar, the
 * parents as links, category search (Ctrl K) on the right, then the KPI pills.
 */
export const CategoryProfileHeader: React.FC<Props> = ({databagId, header: h, money, onBack, onOpen}) => {
    const k = h.kpis;
    const savings = k.costSavingsAchieved;
    const crumbs = breadcrumb(h.path);
    const savingsTip = savings && [
        `Captured (EBIT effective${savings.year ? ` in ${savings.year}` : ''}) in the Savings Initiative Tracker`,
        `Unlocked: ${money(savings.unlocked)} across ${savings.initiatives} initiatives`,
        savings.fromAncestor && `Recorded in SIT at ${savings.fromAncestor.split('|').join(' › ')}`,
        savings.hiddenConfidential > 0 && `${savings.hiddenConfidential} confidential initiatives not included`,
    ].filter(Boolean).join('. ');

    return (
        <Stack gap={2}>
            <Stack direction={{xs: 'column', md: 'row'}} alignItems={{md: 'center'}} justifyContent="space-between" gap={1.5}>
                {/* Back link, the category's name and its risk flags live in the app's top bar (Mithra 3.1 header) */}
                <PageHeader
                    back={{label: 'Explore Categories', onClick: onBack}}
                    title={crumbs[crumbs.length - 1]?.label}
                    tag={h.flags.length > 0 && h.flags.map(f => (
                        <Tooltip key={f} title={FLAG[f].tip(h)}>
                            <span><Tag size="small" color={f === 'single_source' ? 'error' : 'warning'} label={FLAG[f].label}/></span>
                        </Tooltip>
                    ))}
                />
                {/* The parents stay here, as links to their own profiles */}
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{minWidth: 0}} component="nav"
                       aria-label="Parent categories">
                    {crumbs.slice(0, -1).map((c, i) => (
                        <Fragment key={c.path}>
                            {i > 0 && <Typography color="text.disabled">›</Typography>}
                            <Link component="button" underline="hover" color="text.secondary" variant="body2"
                                  onClick={() => onOpen(c.path)}>{c.label}</Link>
                        </Fragment>
                    ))}
                </Stack>
                <CategorySearch databagId={databagId} onOpen={onOpen}/>
                <SettingsLink databagId={databagId}/>
            </Stack>
            <KpiRow>
                <StatChip icon={{element: <DollarCoinIcon/>, color: COLORS.success.main}} title="Category spend"
                          value={`${money(k.spend)}${k.yoyPct === null ? '' : ` · ${k.yoyPct >= 0 ? '+' : ''}${k.yoyPct.toFixed(0)}% YoY`}`}/>
                {k.showSpendUnderManagement && <StatChip icon={{element: <NoteIcon/>, color: COLORS.primary.main}} title="Spend under management"
                                                         value={k.poSharePct === null ? '—' : `${money(k.poSpend)} · ${k.poSharePct.toFixed(0)}%`}
                                                         tooltip={{type: 'info', text: 'Spend on lines with a purchase order'}}/>}
                {savings && (
                    <StatChip icon={{element: <ChartIcon/>, color: COLORS.secondary.main}} title="Cost savings achieved"
                              value={money(savings.captured)} tooltip={{type: 'info', text: savingsTip || ''}}/>
                )}
                <StatChip icon={{element: <DbIcon/>, color: COLORS.info.main}} title="Suppliers"
                          value={k.suppliers.toLocaleString()}/>
            </KpiRow>
        </Stack>
    );
};
